// ─────────────────────────────────────────────────────────────────────────────
// Maintenance & action-recommendation data (prototype 05).
//
// Two layers, deliberately kept apart:
//
//   1. DETERMINISTIC — cycle aggregation and condition trending. Plain
//      arithmetic on the unit profile. No model, no judgement, same answer
//      every time. This is the layer that would be replaced by the native
//      forecasting model.
//
//   2. ORCHESTRATION — the recommendations. Composed from the deterministic
//      output plus maintenance history, inspection notes and depot slots.
//      Illustrative for the demo: the values here are authored, not inferred.
//
// SCOPE: nothing here predicts a failure date. The trend states a degradation
// RATE and a forecast cone that widens with horizon; the recommendation is
// opportunistic (bundle the work into a slot the unit is already booked into).
//
// ANONYMISATION: door labels use the new X1…X4 scheme (see doorLabel below).
// Depots, order references and note text are fictional.
// ─────────────────────────────────────────────────────────────────────────────

import { profileFor } from './fleet';
import { DOOR_IDS, doorHealth, doorLabel } from './doors';
import {
  WINDOW_DAYS, DOW_FACTOR, dateForIndex, formatDay, isoForIndex, indexForIso,
  PERIOD_KEYS, PeriodKey, PeriodSelection, defaultPeriod, indicesFor,
} from './period';

// Calendar and period handling live in data/period.ts so the fleet overview
// can aggregate over the same window. Re-exported for convenience.
export {
  WINDOW_DAYS, dateForIndex, formatDay, isoForIndex, indexForIso,
  PERIOD_KEYS, defaultPeriod, indicesFor,
};
export type { PeriodKey, PeriodSelection };

// Door labelling now lives in data/doors.ts so every tab shares one mapping.
export { doorLabel };

export function doorCar(id: string): string {
  return id[0];
}

/** Kept internal to the data layer — never surfaced as a label. */
export function isPlatformSide(id: string): boolean {
  return id.endsWith('L');
}

// ── Deterministic layer ──────────────────────────────────────────────────────

export const FORECAST_DAYS = 30;

function rnd(n: number): number {
  return Math.abs(Math.sin(n * 9301 + 49297) * 233280) % 1;
}

/** Door cycles on one day. Deterministic per unit/door/day. */
export function dailyCycles(unitId: string, doorId: string, i: number): number {
  const p = profileFor(unitId);
  const base = (isPlatformSide(doorId) ? 208 : 84) * p.loadScale * p.cyclesScale;
  const dow = DOW_FACTOR[dateForIndex(i).getDay()];
  const seed = p.seedOffset + doorId.charCodeAt(0) * 31 + doorId.charCodeAt(1) * 7 + i * 3;
  return Math.round(base * dow * (0.86 + rnd(seed) * 0.28));
}

/** Total cycles for one door over the selected period. */
export function cyclesFor(unitId: string, doorId: string, sel: PeriodSelection): number {
  return indicesFor(sel).reduce((a, i) => a + dailyCycles(unitId, doorId, i), 0);
}

export interface DoorRow {
  id: string;
  label: string;
  car: string;
  cycles: number;
  health: number;
  /** Peak closing current, latest reading. */
  current: number;
  /** Change in peak current per week, in amps. */
  ratePerWeek: number;
}

/** All 16 doors for the selected period, worst condition first. */
export function getDoorRows(unitId: string, sel: PeriodSelection): DoorRow[] {
  return DOOR_IDS
    .map(id => {
      const trend = getTrend(unitId, id);
      return {
        id,
        label: doorLabel(id),
        car: doorCar(id),
        cycles: cyclesFor(unitId, id, sel),
        health: doorHealth(unitId, id),
        current: trend.history[trend.history.length - 1].value,
        ratePerWeek: trend.ratePerWeek,
      };
    })
    .sort((a, b) => a.health - b.health);
}

// ── Condition trend ──────────────────────────────────────────────────────────
// Metric: peak motor current on close. Rises as drag increases — the signal
// Johannes described as the door trending toward anomalous current flow.

export const BASELINE_CURRENT = 4.1;   // A, nominal for a healthy door
export const ADVISORY_CURRENT = 5.2;   // A, inspection advisory

export interface TrendSeries {
  history: Array<{ i: number; value: number }>;
  forecast: Array<{ i: number; mid: number; lo: number; hi: number }>;
  /** Amps per week, positive = degrading. */
  ratePerWeek: number;
  /** Days until the mid forecast reaches the advisory line, or null if beyond horizon. */
  daysToAdvisory: number | null;
  /** Forecast spread at the horizon, in amps — how wide the cone gets. */
  horizonSpread: number;
}

export function getTrend(unitId: string, doorId: string): TrendSeries {
  const p = profileFor(unitId);
  const health = doorHealth(unitId, doorId);
  const misuseLed = p.misuseScale > 1.5;

  // A healthy door is flat; a degrading one climbs. Misuse-led units climb in
  // steps rather than smoothly.
  const slope = Math.max(0, 88 - health) * 0.00082 * (misuseLed ? 1.2 : 1);
  const seed = p.seedOffset + doorId.charCodeAt(0) * 17 + doorId.charCodeAt(1) * 11;

  const history = Array.from({ length: WINDOW_DAYS }, (_, i) => {
    const step = misuseLed && i > WINDOW_DAYS * 0.45 ? slope * 14 : 0;
    const noise = (rnd(seed + i * 5) - 0.5) * 0.14;
    const value = BASELINE_CURRENT + slope * i + step + noise;
    return { i, value: Math.round(value * 100) / 100 };
  });

  const last = history[history.length - 1].value;
  const ratePerWeek = Math.round(slope * 7 * 1000) / 1000;

  const forecast = Array.from({ length: FORECAST_DAYS + 1 }, (_, k) => {
    const mid = last + slope * k;
    // Cone widens with horizon: accuracy degrades, direction holds.
    const spread = 0.035 * Math.sqrt(k) + slope * k * 0.35;
    return {
      i: WINDOW_DAYS - 1 + k,
      mid: Math.round(mid * 100) / 100,
      lo: Math.round((mid - spread) * 100) / 100,
      hi: Math.round((mid + spread) * 100) / 100,
    };
  });

  const hit = forecast.find(f => f.mid >= ADVISORY_CURRENT);
  const horizon = forecast[forecast.length - 1];

  return {
    history,
    forecast,
    ratePerWeek,
    daysToAdvisory: hit ? hit.i - (WINDOW_DAYS - 1) : null,
    horizonSpread: Math.round((horizon.hi - horizon.lo) * 100) / 100,
  };
}

// ── Triage ───────────────────────────────────────────────────────────────────
// The deterministic layer raises far more flags than are worth acting on.
// Suppressing the benign ones is what keeps the orchestration layer cheap.

export interface Triage {
  flags: number;
  actionable: number;
  suppressed: number;
}

export function getTriage(unitId: string): Triage {
  const p = profileFor(unitId);
  const rows = DOOR_IDS.map(id => doorHealth(unitId, id));
  const flags = rows.filter(h => h < 86).length + Math.round(6 * p.misuseScale);
  const actionable = getRecommendations(unitId).length;
  return { flags, actionable, suppressed: Math.max(0, flags - actionable) };
}

// ── Orchestration layer ──────────────────────────────────────────────────────

export type RecKind = 'bundle' | 'verify' | 'external';
export type RecPriority = 'high' | 'medium' | 'low';

export interface EvidenceItem {
  /** i18n key for the evidence label. */
  labelKey: string;
  /** Already-formatted value, or an i18n key when `valueIsKey`. */
  value: string;
  valueIsKey?: boolean;
  /** i18n key naming the system the evidence came from. */
  sourceKey: string;
  /** True when this item came from the deterministic layer. */
  deterministic: boolean;
}

export interface Recommendation {
  id: string;
  kind: RecKind;
  doorId: string;
  doorLabel: string;
  priority: RecPriority;
  /** i18n key for the one-line recommendation. */
  titleKey: string;
  vars: Record<string, string | number>;
  evidence: EvidenceItem[];
  /** Ordered reasoning trace — what the agent did, in sequence. */
  reasoning: Array<{ textKey: string; vars?: Record<string, string | number> }>;
  slot: { date: string; depot: string; reasonKey: string; durationMin: number };
  checklistKeys: string[];
  orderRef: string;
}

const DEPOTS = ['Nord', 'Zentral', 'Süd', 'West'];

/** A depot slot the unit is already booked into, n days out. */
function slotDate(unitId: string, offsetDays: number, locale = 'de-DE'): string {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: 'short' });
}

/**
 * Recommendations for a unit. Derived from the profile's watch doors so each
 * unit tells a different story, consistent with the other prototypes.
 */
export function getRecommendations(unitId: string, locale = 'de-DE'): Recommendation[] {
  const p = profileFor(unitId);
  const watch = Object.keys(p.watchDoors).sort((a, b) => p.watchDoors[a] - p.watchDoors[b]);
  if (watch.length === 0) return [];

  const depot = DEPOTS[Math.floor(rnd(p.seedOffset + 5) * DEPOTS.length)];
  const out: Recommendation[] = [];

  // ── 1. Opportunistic bundling — the worst door, against an existing slot ──
  const worst = watch[0];
  const worstTrend = getTrend(unitId, worst);
  const bundleOffset = 9 + Math.floor(rnd(p.seedOffset + 11) * 12);

  out.push({
    id: `${unitId}-bundle`,
    kind: 'bundle',
    doorId: worst,
    doorLabel: doorLabel(worst),
    priority: 'high',
    titleKey: 'p5.rec.bundle.title',
    vars: { door: doorLabel(worst), date: slotDate(unitId, bundleOffset, locale) },
    evidence: [
      {
        labelKey: 'p5.ev.rate',
        value: `+${worstTrend.ratePerWeek.toFixed(2)} A/w`,
        sourceKey: 'p5.src.stats',
        deterministic: true,
      },
      {
        labelKey: 'p5.ev.current',
        value: `${worstTrend.history[worstTrend.history.length - 1].value.toFixed(2)} A`,
        sourceKey: 'p5.src.stats',
        deterministic: true,
      },
      {
        labelKey: 'p5.ev.notes',
        value: 'p5.note.roller',
        valueIsKey: true,
        sourceKey: 'p5.src.notes',
        deterministic: false,
      },
      {
        labelKey: 'p5.ev.slot',
        value: `${slotDate(unitId, bundleOffset, locale)} · ${depot}`,
        sourceKey: 'p5.src.depot',
        deterministic: false,
      },
      {
        labelKey: 'p5.ev.parts',
        value: 'p5.parts.inStock',
        valueIsKey: true,
        sourceKey: 'p5.src.cmms',
        deterministic: false,
      },
    ],
    reasoning: [
      { textKey: 'p5.why.bundle.1', vars: { door: doorLabel(worst), rate: worstTrend.ratePerWeek.toFixed(2) } },
      { textKey: 'p5.why.bundle.2' },
      { textKey: 'p5.why.bundle.3', vars: { date: slotDate(unitId, bundleOffset, locale), depot } },
      { textKey: 'p5.why.bundle.4' },
    ],
    slot: {
      date: slotDate(unitId, bundleOffset, locale),
      depot,
      reasonKey: 'p5.slot.alreadyBooked',
      durationMin: 40,
    },
    checklistKeys: ['p5.chk.roller', 'p5.chk.rail', 'p5.chk.belt', 'p5.chk.recalibrate', 'p5.chk.recycle'],
    orderRef: `WO-${unitId.replace('-', '')}-${String(100 + Math.floor(rnd(p.seedOffset + 21) * 800))}`,
  });

  // ── 2. Closed-loop verification — did the last repair hold? ───────────────
  const second = watch[1] ?? watch[0];
  const secondTrend = getTrend(unitId, second);

  out.push({
    id: `${unitId}-verify`,
    kind: 'verify',
    doorId: second,
    doorLabel: doorLabel(second),
    priority: p.misuseScale > 1.5 ? 'high' : 'medium',
    titleKey: 'p5.rec.verify.title',
    vars: { door: doorLabel(second), days: 11 },
    evidence: [
      {
        labelKey: 'p5.ev.intervention',
        value: slotDate(unitId, -22, locale),
        sourceKey: 'p5.src.cmms',
        deterministic: false,
      },
      {
        labelKey: 'p5.ev.postRepair',
        value: 'p5.post.resumed',
        valueIsKey: true,
        sourceKey: 'p5.src.stats',
        deterministic: true,
      },
      {
        labelKey: 'p5.ev.rate',
        value: `+${secondTrend.ratePerWeek.toFixed(2)} A/w`,
        sourceKey: 'p5.src.stats',
        deterministic: true,
      },
    ],
    reasoning: [
      { textKey: 'p5.why.verify.1', vars: { door: doorLabel(second), date: slotDate(unitId, -22, locale) } },
      { textKey: 'p5.why.verify.2', vars: { days: 11 } },
      { textKey: 'p5.why.verify.3' },
    ],
    slot: {
      date: slotDate(unitId, 4, locale),
      depot,
      reasonKey: 'p5.slot.reinspect',
      durationMin: 20,
    },
    checklistKeys: ['p5.chk.reviewOrder', 'p5.chk.partBatch', 'p5.chk.rail', 'p5.chk.recycle'],
    orderRef: `WO-${unitId.replace('-', '')}-${String(100 + Math.floor(rnd(p.seedOffset + 33) * 800))}`,
  });

  // ── 3. External factor — degradation tracks a duty pattern, not the door ──
  const third = watch[2] ?? watch[watch.length - 1];

  out.push({
    id: `${unitId}-external`,
    kind: 'external',
    doorId: third,
    doorLabel: doorLabel(third),
    priority: 'low',
    titleKey: 'p5.rec.external.title',
    vars: { door: doorLabel(third) },
    evidence: [
      {
        labelKey: 'p5.ev.correlation',
        value: 'p5.corr.dwell',
        valueIsKey: true,
        sourceKey: 'p5.src.stats',
        deterministic: true,
      },
      {
        labelKey: 'p5.ev.duty',
        value: 'p5.duty.peak',
        valueIsKey: true,
        sourceKey: 'p5.src.timetable',
        deterministic: false,
      },
      {
        labelKey: 'p5.ev.peers',
        value: 'p5.peers.sameRoute',
        valueIsKey: true,
        sourceKey: 'p5.src.stats',
        deterministic: true,
      },
    ],
    reasoning: [
      { textKey: 'p5.why.external.1', vars: { door: doorLabel(third) } },
      { textKey: 'p5.why.external.2' },
      { textKey: 'p5.why.external.3' },
    ],
    slot: {
      date: slotDate(unitId, 30, locale),
      depot,
      reasonKey: 'p5.slot.noAction',
      durationMin: 0,
    },
    checklistKeys: ['p5.chk.dutyReview', 'p5.chk.compareFleet'],
    orderRef: '—',
  });

  return out;
}
